import { useSyncExternalStore } from 'react'

import type { WorkspaceModule } from '../workspace.ts'

import {
  DEFAULT_WINDOW,
  cascadeRect,
  clampRect,
  tileRects,
  type Rect,
  type Size,
} from './geometry.ts'
import {
  MAX_WINDOWS,
  isSingleInstance,
  opensMaximized,
} from './window-defaults.ts'

/**
 * As janelas abertas no Modo PC — F138.
 *
 * ## Por que um store fora do React
 *
 * Mover uma janela não pode redesenhar as outras. Num `useState` no topo da
 * área de trabalho, arrastar Produtos re-renderizaria a lista de Clientes e o
 * PDV a cada soltar do mouse — a "re-render global" que a Master Specification
 * (§27) proíbe no hardware de balcão. Aqui cada janela lê só a própria entrada
 * (`useDesktopSelector`), e o reducer preserva a identidade de toda janela que
 * uma ação não tocou.
 *
 * ## O que NÃO mora aqui
 *
 * O que está dentro da janela — a rota atual, o formulário aberto, a janela
 * filha — é da tela e do roteador dela. O store sabe onde a janela está, de que
 * tamanho, em que estado e em cima de quem.
 */

export type WindowMode = 'normal' | 'minimized' | 'maximized'

export type DesktopWindow = {
  readonly id: string
  readonly module: WorkspaceModule | null
  /** Caminho e busca da rota que a janela está mostrando. */
  readonly href: string
  readonly title: string
  /** 1 para a primeira janela de um módulo, 2 para a segunda… */
  readonly instance: number
  /** A geometria "normal": para onde a janela volta ao sair de maximizada. */
  readonly rect: Rect
  readonly mode: WindowMode
  /** Minimizada a partir de maximizada: restaurar devolve maximizada. */
  readonly wasMaximized: boolean
  readonly z: number
}

export type DesktopState = {
  readonly windows: readonly DesktopWindow[]
  readonly activeId: string | null
  /** Ids da mais recente para a mais antiga — o Ctrl+Tab e o menu Janela. */
  readonly mru: readonly string[]
  readonly bounds: Size
  readonly nextId: number
  readonly nextZ: number
  /** Quantas janelas já foram abertas: a posição da próxima na cascata. */
  readonly opened: number
  /** O aviso da barra de tarefas, quando há um. */
  readonly notice: string | null
}

export type OpenRequest = {
  readonly module: WorkspaceModule | null
  readonly href: string
  readonly title: string
  /** Outra janela do mesmo módulo, em vez de trazer a que já existe. */
  readonly newInstance?: boolean | undefined
}

/** Uma janela guardada neste computador, pronta para voltar (F138). */
export type HydratedWindow = {
  readonly module: WorkspaceModule | null
  readonly href: string
  readonly title: string
  readonly rect: Rect
  readonly mode: WindowMode
  readonly wasMaximized: boolean
}

export type DesktopAction =
  | { readonly type: 'open'; readonly request: OpenRequest }
  | { readonly type: 'focus'; readonly id: string }
  | { readonly type: 'close'; readonly id: string }
  | {
      readonly type: 'move'
      readonly id: string
      readonly x: number
      readonly y: number
    }
  | { readonly type: 'resize'; readonly id: string; readonly rect: Rect }
  | { readonly type: 'minimize'; readonly id: string }
  | { readonly type: 'maximize'; readonly id: string }
  | { readonly type: 'restore'; readonly id: string }
  | { readonly type: 'toggleMaximize'; readonly id: string }
  | {
      readonly type: 'navigated'
      readonly id: string
      readonly href: string
      readonly title: string
      readonly module: WorkspaceModule | null
    }
  | { readonly type: 'cascade' }
  | { readonly type: 'tile' }
  | { readonly type: 'minimizeAll' }
  | { readonly type: 'closeAll' }
  | { readonly type: 'setBounds'; readonly bounds: Size }
  | { readonly type: 'dismissNotice' }
  /** Ctrl+Tab (1) e Ctrl+Shift+Tab (-1): alternar entre as janelas. */
  | { readonly type: 'cycle'; readonly direction: 1 | -1 }
  /**
   * As janelas guardadas, da de trás para a da frente. Substitui as que houver.
   * `activeIndex` aponta para uma posição de `windows`.
   */
  | {
      readonly type: 'hydrate'
      readonly windows: readonly HydratedWindow[]
      readonly activeIndex: number | null
    }

export const LIMIT_NOTICE = `${String(MAX_WINDOWS)} janelas abertas — feche uma para abrir outra.`

/** Acima disto os `z-index` são recompactados para 1…n. */
const Z_CEILING = 10_000

export const initialDesktopState = (
  bounds: Size = { w: 1280, h: 640 }
): DesktopState => ({
  windows: [],
  activeId: null,
  mru: [],
  bounds,
  nextId: 1,
  nextZ: 1,
  opened: 0,
  notice: null,
})

const pathOf = (href: string): string => href.split(/[?#]/)[0] ?? href

/** O título com o número da instância, quando há mais de uma. */
export const windowLabel = (window: DesktopWindow): string =>
  window.instance > 1
    ? `${window.title} (${String(window.instance)})`
    : window.title

// ---------------------------------------------------------------------------
// O que fazer com um pedido de "abrir"
// ---------------------------------------------------------------------------

export type OpenPlan =
  | { readonly kind: 'focus'; readonly id: string }
  | { readonly kind: 'create' }
  | { readonly kind: 'limit' }

/**
 * Abrir uma tela que já tem janela traz a janela, em vez de abrir outra.
 *
 * É o que se espera de um menu: clicar em Produtos duas vezes não deixa duas
 * listas iguais empilhadas. Outra janela do mesmo módulo só com pedido
 * explícito (`newInstance`), e nunca para as telas de janela única.
 *
 * Separado do reducer porque quem pede às vezes precisa saber a resposta antes
 * de despachar: abrir um REGISTRO numa janela que já existe navega aquela
 * janela — e isso é o roteador dela, que o store não conhece.
 */
export const planOpen = (
  state: DesktopState,
  request: OpenRequest
): OpenPlan => {
  const same = state.windows.filter((window) =>
    request.module === null
      ? window.module === null && pathOf(window.href) === pathOf(request.href)
      : window.module === request.module
  )

  if (
    same.length > 0 &&
    (request.newInstance !== true || isSingleInstance(request.module))
  ) {
    const recent = state.mru.find((id) =>
      same.some((window) => window.id === id)
    )
    return { kind: 'focus', id: recent ?? same[0]?.id ?? '' }
  }

  if (state.windows.length >= MAX_WINDOWS) return { kind: 'limit' }

  return { kind: 'create' }
}

// ---------------------------------------------------------------------------
// O reducer
// ---------------------------------------------------------------------------

const withMru = (mru: readonly string[], id: string): string[] => [
  id,
  ...mru.filter((entry) => entry !== id),
]

/** A janela visível usada mais recentemente, fora `except`. */
const nextActive = (
  windows: readonly DesktopWindow[],
  mru: readonly string[],
  except: string
): string | null =>
  mru.find(
    (id) =>
      id !== except &&
      windows.some((window) => window.id === id && window.mode !== 'minimized')
  ) ?? null

const replace = (
  state: DesktopState,
  id: string,
  change: (window: DesktopWindow) => DesktopWindow
): readonly DesktopWindow[] =>
  state.windows.map((window) => (window.id === id ? change(window) : window))

/** Recompacta os `z-index` quando crescem demais, preservando a ordem. */
const compactZ = (state: DesktopState): DesktopState => {
  if (state.nextZ < Z_CEILING) return state

  const order = [...state.windows].sort((a, b) => a.z - b.z)
  const rank = new Map(order.map((window, index) => [window.id, index + 1]))

  return {
    ...state,
    windows: state.windows.map((window) => ({
      ...window,
      z: rank.get(window.id) ?? window.z,
    })),
    nextZ: order.length + 1,
  }
}

const focus = (state: DesktopState, id: string): DesktopState => {
  const target = state.windows.find((window) => window.id === id)
  if (target === undefined) return state

  const onTop =
    state.activeId === id &&
    target.mode !== 'minimized' &&
    target.z === state.nextZ - 1
  if (onTop) return state

  return compactZ({
    ...state,
    windows: replace(state, id, (window) => ({
      ...window,
      z: state.nextZ,
      mode:
        window.mode === 'minimized'
          ? window.wasMaximized
            ? 'maximized'
            : 'normal'
          : window.mode,
    })),
    activeId: id,
    mru: withMru(state.mru, id),
    nextZ: state.nextZ + 1,
  })
}

const smallestFreeInstance = (
  windows: readonly DesktopWindow[],
  module: WorkspaceModule | null,
  href: string
): number => {
  const taken = new Set(
    windows
      .filter((window) =>
        module === null
          ? window.module === null && pathOf(window.href) === pathOf(href)
          : window.module === module
      )
      .map((window) => window.instance)
  )
  let instance = 1
  while (taken.has(instance)) instance += 1
  return instance
}

export const desktopReducer = (
  state: DesktopState,
  action: DesktopAction
): DesktopState => {
  switch (action.type) {
    case 'open': {
      const plan = planOpen(state, action.request)
      if (plan.kind === 'focus') return focus(state, plan.id)
      if (plan.kind === 'limit') return { ...state, notice: LIMIT_NOTICE }

      const { module, href, title } = action.request
      const id = `w${String(state.nextId)}`
      const window: DesktopWindow = {
        id,
        module,
        href,
        title,
        instance: smallestFreeInstance(state.windows, module, href),
        rect: cascadeRect(state.opened, DEFAULT_WINDOW, state.bounds),
        mode: opensMaximized(module, pathOf(href)) ? 'maximized' : 'normal',
        wasMaximized: false,
        z: state.nextZ,
      }

      return compactZ({
        ...state,
        windows: [...state.windows, window],
        activeId: id,
        mru: withMru(state.mru, id),
        nextId: state.nextId + 1,
        nextZ: state.nextZ + 1,
        opened: state.opened + 1,
        notice: null,
      })
    }

    case 'focus':
      return focus(state, action.id)

    case 'close': {
      if (!state.windows.some((window) => window.id === action.id)) {
        return state
      }
      const windows = state.windows.filter((window) => window.id !== action.id)
      const mru = state.mru.filter((id) => id !== action.id)
      return {
        ...state,
        windows,
        mru,
        activeId:
          state.activeId === action.id
            ? nextActive(windows, mru, action.id)
            : state.activeId,
        notice: null,
      }
    }

    case 'move':
      return {
        ...state,
        windows: replace(state, action.id, (window) => ({
          ...window,
          rect: clampRect(
            { ...window.rect, x: action.x, y: action.y },
            state.bounds
          ),
        })),
      }

    case 'resize':
      return {
        ...state,
        windows: replace(state, action.id, (window) => ({
          ...window,
          rect: clampRect(action.rect, state.bounds),
          mode: window.mode === 'maximized' ? 'normal' : window.mode,
        })),
      }

    case 'minimize': {
      const target = state.windows.find((window) => window.id === action.id)
      if (target === undefined || target.mode === 'minimized') return state
      const windows = replace(state, action.id, (window) => ({
        ...window,
        mode: 'minimized',
        wasMaximized: window.mode === 'maximized',
      }))
      return {
        ...state,
        windows,
        activeId:
          state.activeId === action.id
            ? nextActive(windows, state.mru, action.id)
            : state.activeId,
      }
    }

    case 'maximize': {
      const focused = focus(state, action.id)
      return {
        ...focused,
        windows: replace(focused, action.id, (window) => ({
          ...window,
          mode: 'maximized',
        })),
      }
    }

    case 'restore': {
      const target = state.windows.find((window) => window.id === action.id)
      if (target === undefined) return state
      if (target.mode === 'minimized') return focus(state, action.id)
      const focused = focus(state, action.id)
      return {
        ...focused,
        windows: replace(focused, action.id, (window) => ({
          ...window,
          mode: 'normal',
          wasMaximized: false,
        })),
      }
    }

    case 'toggleMaximize': {
      const target = state.windows.find((window) => window.id === action.id)
      if (target === undefined) return state
      return desktopReducer(state, {
        type: target.mode === 'maximized' ? 'restore' : 'maximize',
        id: action.id,
      })
    }

    case 'navigated':
      return {
        ...state,
        windows: replace(state, action.id, (window) =>
          window.href === action.href &&
          window.title === action.title &&
          window.module === action.module
            ? window
            : {
                ...window,
                href: action.href,
                title: action.title,
                module: action.module,
              }
        ),
      }

    case 'cascade': {
      const visible = state.windows
        .filter((window) => window.mode !== 'minimized')
        .sort((a, b) => a.z - b.z)
      const index = new Map(visible.map((window, i) => [window.id, i]))
      return {
        ...state,
        windows: state.windows.map((window) => {
          const position = index.get(window.id)
          return position === undefined
            ? window
            : {
                ...window,
                mode: 'normal',
                wasMaximized: false,
                rect: cascadeRect(position, DEFAULT_WINDOW, state.bounds),
              }
        }),
        opened: visible.length,
      }
    }

    case 'tile': {
      const visible = state.windows
        .filter((window) => window.mode !== 'minimized')
        .sort((a, b) => a.z - b.z)
      const rects = tileRects(visible.length, state.bounds)
      const byId = new Map(
        visible.map((window, i) => [window.id, rects[i]] as const)
      )
      return {
        ...state,
        windows: state.windows.map((window) => {
          const rect = byId.get(window.id)
          return rect === undefined
            ? window
            : { ...window, mode: 'normal', wasMaximized: false, rect }
        }),
      }
    }

    case 'minimizeAll':
      return {
        ...state,
        windows: state.windows.map((window) =>
          window.mode === 'minimized'
            ? window
            : {
                ...window,
                mode: 'minimized',
                wasMaximized: window.mode === 'maximized',
              }
        ),
        activeId: null,
      }

    case 'closeAll':
      return {
        ...state,
        windows: [],
        activeId: null,
        mru: [],
        opened: 0,
        notice: null,
      }

    case 'setBounds': {
      if (
        action.bounds.w === state.bounds.w &&
        action.bounds.h === state.bounds.h
      ) {
        return state
      }
      return {
        ...state,
        bounds: action.bounds,
        windows: state.windows.map((window) => {
          const rect = clampRect(window.rect, action.bounds)
          const same =
            rect.x === window.rect.x &&
            rect.y === window.rect.y &&
            rect.w === window.rect.w &&
            rect.h === window.rect.h
          return same ? window : { ...window, rect }
        }),
      }
    }

    case 'dismissNotice':
      return state.notice === null ? state : { ...state, notice: null }

    /*
     * Alternar pela lista de recentes, como o Alt+Tab do sistema: Ctrl+Tab vai
     * para a janela usada ANTES da atual — e apertado de novo volta, que é o
     * vai-e-vem entre duas janelas que se faz o dia inteiro. Com Shift, vai
     * para a mais antiga. Uma minimizada volta ao ser escolhida.
     */
    case 'cycle': {
      const order = state.mru.filter((id) =>
        state.windows.some((window) => window.id === id)
      )
      const first = order[0]
      if (first === undefined) return state

      // Sem janela ativa (todas minimizadas), a primeira tecla traz a última.
      if (state.activeId !== first) return focus(state, first)

      const target = action.direction === 1 ? order[1] : order[order.length - 1]
      return target === undefined || target === first
        ? state
        : focus(state, target)
    }

    /*
     * Reabrir o Aeris e achar as janelas onde estavam.
     *
     * As mesmas regras de abrir valem aqui: nunca mais que o limite, e uma
     * janela só para as telas de janela única — um layout guardado por uma
     * versão anterior não fura a regra. A geometria é reajustada à tela de
     * agora, que pode ser outro monitor. A ativa volta na frente; uma que
     * estava minimizada não volta ativa.
     */
    case 'hydrate': {
      const windows: DesktopWindow[] = []
      const idByInput = new Map<number, string>()

      action.windows.forEach((entry, index) => {
        if (windows.length >= MAX_WINDOWS) return
        if (
          isSingleInstance(entry.module) &&
          windows.some((window) => window.module === entry.module)
        ) {
          return
        }

        const id = `w${String(state.nextId + windows.length)}`
        idByInput.set(index, id)
        windows.push({
          id,
          module: entry.module,
          href: entry.href,
          title: entry.title,
          instance: smallestFreeInstance(windows, entry.module, entry.href),
          rect: clampRect(entry.rect, state.bounds),
          mode: entry.mode,
          wasMaximized: entry.wasMaximized,
          z: windows.length + 1,
        })
      })

      const candidate =
        action.activeIndex === null
          ? undefined
          : idByInput.get(action.activeIndex)
      const activeId =
        candidate !== undefined &&
        windows.find((window) => window.id === candidate)?.mode !== 'minimized'
          ? candidate
          : null

      const stacked = windows.map((window) =>
        window.id === activeId ? { ...window, z: windows.length + 1 } : window
      )
      const byRecency = [...stacked]
        .sort((a, b) => b.z - a.z)
        .map((window) => window.id)

      return {
        ...state,
        windows: stacked,
        activeId,
        mru: byRecency,
        nextId: state.nextId + windows.length,
        nextZ: windows.length + 2,
        opened: windows.length,
        notice: null,
      }
    }
  }
}

// ---------------------------------------------------------------------------
// O store
// ---------------------------------------------------------------------------

export type DesktopStore = {
  readonly getState: () => DesktopState
  readonly dispatch: (action: DesktopAction) => void
  readonly subscribe: (listener: () => void) => () => void
}

export const createDesktopStore = (
  initial: DesktopState = initialDesktopState()
): DesktopStore => {
  let state = initial
  const listeners = new Set<() => void>()

  return {
    getState: () => state,
    dispatch: (action) => {
      const next = desktopReducer(state, action)
      if (next === state) return
      state = next
      for (const listener of listeners) listener()
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/**
 * Uma fatia do estado, e só ela.
 *
 * O seletor precisa devolver um valor estável — a própria janela, o próprio
 * array —, nunca um objeto montado na hora: `useSyncExternalStore` compara por
 * identidade e um objeto novo a cada leitura é uma renderização sem fim.
 */
export const useDesktopSelector = <T>(
  store: DesktopStore,
  selector: (state: DesktopState) => T
): T => useSyncExternalStore(store.subscribe, () => selector(store.getState()))
